'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { CollabCard } from "@/stories/components/CollabCard/CollabCard";
import { Input } from "@/stories/components/Form/Input/Input";
import { Select } from "@/stories/components/Form/Select/Select";
import { Filters } from "../Search/Filters/Filters";
import { Button } from "@/stories/atoms/Button/Button";
import { Icon } from "@/stories/atoms/Icon/Icon";
import { Grid } from "../Grid/Grid";

import useIsMobile from "@/hooks/useMobile";
import { useModal } from "../Modal/Modal";
import { FiltersDialog } from "../Search/SearchPage/FiltersDialog";
import styles from './collab-listing.module.css'
import { Row } from "../Grid/Row";
import { CustomSelect } from "@/stories/components/Form/CustomSelect/CustomSelect";
import { getCollabFonction } from "@/domains/collaborators/collaborators.utils";
import { TypeCollabFonction } from "@/domains/collaborators/collaborators.type";

type CollabListingCollab = {
  id: string,
  nom: string,
  prenom: string,
  fonction: TypeCollabFonction,
  photo?: string,
  agenceCle: string,
  civilite: string,
  telephonePortable?: string,
  secteurs?: {
    codeDepartement?: string,
    departement?: string,
    codeInsee?: string,
  }[],
}

type CollabListingSector = {
  ville?: string,
  codeInsee?: string,
  codeDepartement?: string,
  departement?: string
}

type CollabListingAgence = {
  nom: string,
  slug?: string,
}

export type CollabListingType = {
  collabs: CollabListingCollab[],
  sectors: CollabListingSector[],
  agencies?: CollabListingAgence[],
  defaultParams?: {
    name?: string,
    city?: string,
    dep?: string,
    fct?: string,
    agency?: string
  }
}

export default function CollabListing({
  collabs,
  sectors,
  agencies,
  defaultParams
}: CollabListingType) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter();
  const params = {
    name: searchParams.get('name') || defaultParams?.name,
    city: searchParams.get('city') || defaultParams?.city,
    dep: searchParams.get('dep') || defaultParams?.dep,
    fct: searchParams.get('fct') || defaultParams?.fct,
    agency: searchParams.get('agency') || defaultParams?.agency
  }

  const [collabList, setCollabList] = useState(collabs)
  const [filters, setFilters] = useState({
    name: params.name ? params.name : '',
    dep: params.dep ? params.dep : '',
    city: params.city ? params.city : '',
    fct: params.fct ? params.fct : '',
    agency: params.agency ? params.agency : '',
  })

  const agenciesList = agencies?.map(agency => ({
    label: `${agency.nom}`,
    value: agency.slug as string
  }))

  // Filter the city list with only cities in the selected department
  const filterCities = useCallback(() => {
    return sectors.reduce((acc, curr) => {
      return [
        ...acc,
        ...((acc.findIndex(item => item.value === curr.codeInsee) === -1) && (filters.dep === '' || filters.dep === curr.codeDepartement) ? [{
          label: curr.ville as string,
          value: curr.codeInsee as string
        }] : [])
      ]
    }, [] as { label: string, value: string }[])
  }, [sectors, filters.dep])

  const cityList = useMemo(() => filterCities(), [filterCities])

  // Reset city if it no longer belongs to the selected department
  if (filters.city !== '' && !cityList.find(c => c.value === filters.city)) {
    setFilters({ ...filters, city: '' })
  }

  // return only the department with collabs
  const depWithCollabs = sectors.reduce((acc, curr) => {
    return [
      ...acc,
      ...(acc.findIndex(item => item.value === curr.codeDepartement) === -1 ? [{
          label: `${curr.departement} (${curr.codeDepartement})`,
          value: curr.codeDepartement as string
        }] : [])
    ]
  }, [] as { label: string, value: string }[])

  // get all collabs fonctions
  const fonctions = collabs.reduce((acc, curr) => {
    const index = acc.findIndex(item => item.value === getCollabFonction(curr.fonction).value)

    if (index === -1) {
      const fonctionName = getCollabFonction(curr.fonction)

      acc.push(fonctionName)
    }

    return acc
  }, [] as { label: string, value: string }[])

  // filter the collab list depending on selected filters
  const doFilter = useCallback(() => {
    let filtered = collabs

    if (filters.agency !== '') {
      filtered = filtered.filter(collab => collab.agenceCle === filters.agency)
    }

    if (filters.name !== '') {
      filtered = filtered.filter(collab => {
        return collab.nom.toLocaleLowerCase().includes(filters.name.toLocaleLowerCase()) || collab.prenom.toLocaleLowerCase().includes(filters.name.toLocaleLowerCase())
      })
    }

    if (filters.dep !== '') {
      filtered = filtered.filter(collab => {
        return collab.secteurs?.findIndex(secteur => secteur.codeDepartement === filters.dep) !== -1
      })
    }

    if (filters.city !== '') {
      filtered = filtered.filter(collab => {
        return collab.secteurs?.findIndex(secteur => secteur.codeInsee === filters.city) !== -1
      })
    }

    if (filters.fct !== '') {
      filtered = filtered.filter(collab => getCollabFonction(collab.fonction).value === filters.fct)
    }

    setCollabList(filtered)
  }, [collabs, filters])

  const filterList = useMemo(() => debounce(doFilter, 300), [doFilter])

  // synchronize Url parameters to state
  const synchroUrl = useCallback(
    () => {
      // Rebuild params from scratch to avoid depending on searchParams
      const next = new URLSearchParams();
      (Object.keys(filters) as Array<keyof typeof filters>).forEach((key) => {
        if (filters[key] !== '') next.set(key, filters[key])
      })

      // Update URL without navigation, no scroll jump
      router.replace(next.size ? `${pathname}?${next}` : pathname, { scroll: false })
    },
    [filters, router, pathname]
  )

  useEffect(() => {
    filterList()
    return () => { filterList.cancel() }
  }, [filterList])

  useEffect(() => {
    synchroUrl()
  }, [synchroUrl])

  useEffect(() => {
    document.body.classList.add('search-page')

    return () => { document.body.classList.remove('search-page') }
  }, [])

  const ref = useRef<HTMLDialogElement | null>(null)
  const { isMobile } = useIsMobile()
  const { setOpen } = useModal({ ref })

  const clear = () => {
    setFilters({
      name: '',
      dep: '',
      city: '',
      fct: '',
      agency: ''
    })
  }

  const findInFilter = (base: { label: string, value: string }[], filter: keyof typeof filters) => {
    return base.find(ag => ag.value === filters[filter])
  }

  return (
    <>
      <Filters>
        {isMobile ? (
          <>
            <Button onClick={() => setOpen(true)}>
              <span className="hide-sub-md">Filtres</span>
              <Icon icon="filter" />
            </Button>
            <FiltersDialog
              ref={ref}
              onClose={() => setOpen(false)}
              actions={
                <>
                  <Button
                    type="button"
                    variant='secondary'
                    onClick={clear} disabled={!Object.values(filters).find(value => value !== '')}
                    >
                    <Icon icon='arrow-rotate-left' />
                    <span className="sr-only">Réinitialiser</span>
                  </Button>
                  <Button
                    onClick={() => setOpen(false)}
                    onAuxClick={() => setOpen(false)}>
                      Affichés les collaborateurs
                  </Button>
                </>
              }
              title='Filtres'>
              {agenciesList && (
                <div className={styles.filters}>
                  <Select
                    variant='secondary'
                    hideOptionnal
                    name='agency'
                    id='agency'
                    label='Agences'
                    value={filters.agency}
                    setValue={agency => setFilters({ ...filters, agency })} options={[
                      {
                        label: 'Toutes les agences',
                        value: '',
                      },
                      ...agenciesList
                    ]}
                  />
                </div>
              )}
              <Input
                variant="secondary"
                hideOptionnal
                name="name"
                id="name"
                icon="user"
                value={filters.name}
                label="Chercher par nom"
                setValue={name => setFilters({ ...filters, name })}
                />
              <Select
                variant='secondary'
                hideOptionnal
                name='dep'
                id='dep'
                label='Département'
                icon="location-dot"
                value={filters.dep}
                setValue={dep => setFilters({ ...filters, dep })}
                options={[
                  {
                    label: 'Tous',
                    value: '',
                  },
                  ...depWithCollabs
                ]}
                />
              <Select
                variant='secondary'
                hideOptionnal
                name='ville'
                label='Ville'
                id='ville'
                icon="location-dot"
                required
                value={filters.city}
                setValue={city => setFilters({ ...filters, city })}
                options={[
                  {
                    label: 'Toutes',
                    value: '',
                  },
                  ...cityList
                ]}
              />
              <Select
                variant='secondary'
                hideOptionnal
                name='fonction'
                label='Fonction'
                id='fonction'
                required
                value={filters.fct}
                setValue={fct => setFilters({ ...filters, fct })}
                options={[
                  {
                    label: 'Toutes',
                    value: '',
                  },
                  ...fonctions
                ]}
                />
            </FiltersDialog>
          </>
        ) : (
          <>
            <Row>
              {agenciesList && (
                <div className={styles.filtersTop}>
                  <CustomSelect
                    variant='secondary'
                    hideOptionnal
                    name='agency'
                    label='Agences'
                    inline
                    id='agency'
                    value={filters.agency
                      ? { label: findInFilter(agenciesList, 'agency')?.label || filters.agency, value: filters.agency }
                      : undefined}
                    setValue={agency => setFilters({ ...filters, agency })}
                    options={[
                      {
                        label: 'Toutes les agences',
                        value: '',
                      },
                      ...agenciesList
                    ]}
                    />
                </div>
              )}
              <Input
                variant="secondary"
                placeholder="Nom"
                hideOptionnal
                name="name"
                id={"name"}
                icon="user"
                value={filters.name}
                label={"Chercher par nom"}
                hideLabel
                setValue={name => setFilters({ ...filters, name })}
                />
              <CustomSelect
                variant='secondary'
                hideOptionnal
                name='dep'
                label='Département'
                id='dep'
                icon="location-dot"
                value={
                  filters.dep
                  ? { label: findInFilter(depWithCollabs, 'dep')?.label || filters.dep, value: filters.dep } 
                  : undefined}
                setValue={dep => setFilters({ ...filters, dep })}
                options={[
                  {
                    label: 'Tous',
                    value: '',
                  },
                  ...depWithCollabs
                ]}
                />
              <CustomSelect
                variant='secondary'
                hideOptionnal
                name='ville'
                label='Ville'
                id='ville'
                icon="location-dot"
                value={
                  filters.city
                  ? { label: findInFilter(cityList, 'city')?.label || filters.city, value: filters.city }
                  : undefined}
                setValue={city => setFilters({ ...filters, city })}
                options={[
                  {
                    label: 'Toutes',
                    value: '',
                  },
                  ...cityList
                ]}
                />
              <CustomSelect
                variant='secondary'
                hideOptionnal
                name='fonction'
                label='Fonction'
                id='fonction'
                value={
                  filters.fct
                  ? { label: findInFilter(fonctions, 'fct')?.label || filters.fct, value: filters.fct }
                  : undefined}
                setValue={fct => setFilters({ ...filters, fct })}
                options={[
                  {
                    label: 'Toutes',
                    value: '',
                  },
                  ...fonctions
                ]}
                />
              <Button variant='secondary' type="button" onClick={clear} disabled={!Object.values(filters).find(value => value !== '')}>
                <Icon icon='arrow-rotate-left' />
                <span className="sr-only">Réinitialiser</span>
              </Button>
            </Row>
            {filters.agency && agenciesList && (
              <span>{collabList.length} résultat{collabList.length > 1 ? 's' : ''} pour l'agence {agencies?.find(ag => ag.slug === filters.agency)?.nom}</span>
            )}
          </>
        )}
      </Filters>
      <Grid nbCols={4}>
        {collabList.length > 0 ?
          collabList.map((collab) => (
            <CollabCard
              key={collab.id}
              nom={collab.nom}
              prenom={collab.prenom}
              fonction={collab.fonction}
              photo={collab.photo}
              id={collab.id}
              />
          ))
          : 'Aucun collaborateur ne corresponds à vos filtres.'}
      </Grid>
    </>
  )
}